import React, { useEffect, useState, useRef } from 'react';
import { DatabaseIcon, DownloadIcon, RefreshCwIcon, TrashIcon, ClockIcon, CheckCircleIcon, AlertTriangleIcon, RotateCcwIcon, SaveIcon, PlayIcon, LockIcon, UploadIcon, CalendarIcon } from 'lucide-react';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { Select } from '../ui/Select';
import { Input } from '../ui/Input';
import { Modal } from '../ui/Modal';
import { Badge } from '../ui/Badge';
import { useAuth } from '../../contexts/AuthContext';
import { getBackupConfig, updateBackupConfig, createBackup, getBackupHistory, restoreBackupFromFile, deleteBackup, cleanOldBackups, formatFileSize, BackupConfig as BackupConfigType, BackupHistory } from '../../utils/backupService';
export function BackupConfig() {
  const {
    user
  } = useAuth();
  const isAdmin = user?.role === 'administrador';
  const [config, setConfig] = useState<BackupConfigType | null>(null);
  const [history, setHistory] = useState<BackupHistory[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [creating, setCreating] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const [saveMessage, setSaveMessage] = useState('');
  // Form state
  const [enabled, setEnabled] = useState(false);
  const [frequency, setFrequency] = useState<'daily' | 'weekly' | 'monthly'>('weekly');
  const [retentionDays, setRetentionDays] = useState(30);
  const [backupHour, setBackupHour] = useState(3);
  const [backupMinute, setBackupMinute] = useState(0);
  // Modal states
  const [isRestoreModalOpen, setIsRestoreModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedBackup, setSelectedBackup] = useState<BackupHistory | null>(null);
  // File upload for restore
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [restoreFile, setRestoreFile] = useState<File | null>(null);
  const [restoreFileError, setRestoreFileError] = useState('');
  useEffect(() => {
    loadData();
  }, []);
  // Note: Auto-backup scheduler is managed by App.tsx globally.
  // No need to start it here - just reload data when config changes.
  const loadData = async () => {
    setLoading(true);
    const [configData, historyData] = await Promise.all([getBackupConfig(), getBackupHistory()]);
    setConfig(configData);
    setEnabled(configData.enabled);
    setFrequency(configData.frequency);
    setRetentionDays(configData.retentionDays);
    setBackupHour(configData.backupHour ?? 3);
    setBackupMinute(configData.backupMinute ?? 0);
    setHistory(historyData);
    setLoading(false);
  };
  const handleSaveConfig = async () => {
    if (!isAdmin) return;
    setSaving(true);
    const currentConfig = config || (await getBackupConfig());
    const success = await updateBackupConfig({
      id: currentConfig.id,
      enabled,
      frequency,
      retentionDays,
      backupHour,
      backupMinute
    });
    if (success) {
      setSaveMessage('✅ Configuración guardada correctamente');
      await loadData();
    } else {
      setSaveMessage('❌ Error al guardar la configuración');
    }
    setTimeout(() => setSaveMessage(''), 3000);
    setSaving(false);
  };
  const handleCreateBackup = async () => {
    if (!user || !isAdmin) return;
    setCreating(true);
    const result = await createBackup(user.id, user.name, 'manual');
    if (result.success) {
      setSaveMessage('✅ Backup creado y descargado exitosamente');
      await loadData();
    } else {
      setSaveMessage(`❌ Error: ${result.error}`);
    }
    setTimeout(() => setSaveMessage(''), 3000);
    setCreating(false);
  };
  const handleOpenRestoreModal = () => {
    setRestoreFile(null);
    setRestoreFileError('');
    setIsRestoreModalOpen(true);
  };
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setRestoreFileError('');
    const file = e.target.files?.[0];
    if (!file) {
      setRestoreFile(null);
      return;
    }
    if (!file.name.endsWith('.json')) {
      setRestoreFileError('El archivo debe ser un archivo JSON (.json)');
      setRestoreFile(null);
      return;
    }
    setRestoreFile(file);
  };
  const handleRestoreFromFile = async () => {
    if (!restoreFile || !user || !isAdmin) return;
    setRestoring(true);
    setRestoreFileError('');
    try {
      const text = await restoreFile.text();
      const backupData = JSON.parse(text);
      if (typeof backupData !== 'object' || backupData === null || Array.isArray(backupData)) {
        throw new Error('El archivo no tiene el formato de backup esperado');
      }
      const result = await restoreBackupFromFile(backupData, user.id, user.name);
      if (result.success) {
        setSaveMessage('✅ Backup restaurado exitosamente. Recargando...');
        setIsRestoreModalOpen(false);
        setTimeout(() => window.location.reload(), 2000);
      } else {
        setRestoreFileError(`Error: ${result.error}`);
      }
    } catch (err: any) {
      setRestoreFileError(err.message || 'Error al leer el archivo de backup');
    }
    setRestoring(false);
  };
  const handleDeleteBackup = async () => {
    if (!selectedBackup || !isAdmin) return;
    const success = await deleteBackup(selectedBackup.id);
    if (success) {
      setSaveMessage('✅ Backup eliminado');
      await loadData();
    } else {
      setSaveMessage('❌ Error al eliminar backup');
    }
    setTimeout(() => setSaveMessage(''), 3000);
    setIsDeleteModalOpen(false);
  };
  const handleCleanOldBackups = async () => {
    if (!isAdmin) return;
    const deleted = await cleanOldBackups(retentionDays);
    setSaveMessage(`✅ ${deleted} backup(s) antiguos eliminados`);
    await loadData();
    setTimeout(() => setSaveMessage(''), 3000);
  };
  const frequencyOptions = [{
    value: 'daily',
    label: 'Diario'
  }, {
    value: 'weekly',
    label: 'Semanal'
  }, {
    value: 'monthly',
    label: 'Mensual'
  }];
  if (!isAdmin) {
    return <Card data-id="element-734">
        <div className="py-16 text-center text-gray-500 dark:text-gray-400" data-id="element-735">
          <LockIcon className="w-16 h-16 mx-auto mb-4 text-gray-300 dark:text-gray-600" data-id="element-736" />
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2" data-id="element-737">
            Acceso Restringido
          </h3>
          <p data-id="element-738">
            Solo los administradores pueden acceder a la configuración de
            backups.
          </p>
        </div>
      </Card>;
  }
  if (loading) {
    return <Card data-id="element-739">
        <div className="py-12 text-center" data-id="element-740">
          <RefreshCwIcon className="w-8 h-8 animate-spin mx-auto text-gray-400 mb-2" data-id="element-741" />
          <p className="text-gray-500" data-id="element-742">Cargando configuración...</p>
        </div>
      </Card>;
  }
  return <div className="space-y-6" data-id="element-743">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4" data-id="element-744">
        <div data-id="element-745">
          <h2 className="text-lg md:text-xl font-bold text-gray-900 dark:text-white flex items-center gap-2" data-id="element-746">
            <DatabaseIcon className="w-6 h-6 text-blue-600 dark:text-blue-400" data-id="element-747" />
            Backup Automático
          </h2>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1" data-id="element-748">
            Configura backups automáticos con versionado para proteger tus datos
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2" data-id="element-749">
          <Button variant="outline" leftIcon={<UploadIcon className="w-4 h-4" data-id="element-751" />} onClick={handleOpenRestoreModal} data-id="element-750">
            Restaurar desde Archivo
          </Button>
          <Button leftIcon={<PlayIcon className="w-4 h-4" data-id="element-753" />} onClick={handleCreateBackup} isLoading={creating} data-id="element-752">
            Crear Backup Ahora
          </Button>
        </div>
      </div>

      {saveMessage && <div className={`p-3 rounded-lg text-sm ${saveMessage.startsWith('✅') ? 'bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 text-green-600 dark:text-green-400' : 'bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-600 dark:text-red-400'}`} data-id="element-754">
          {saveMessage}
        </div>}

      {/* Info banner about file-based backups */}
      <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-xl border border-blue-100 dark:border-blue-800" data-id="element-755">
        <div className="flex items-start gap-3" data-id="element-756">
          <DownloadIcon className="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" data-id="element-757" />
          <div data-id="element-758">
            <p className="text-sm font-medium text-blue-800 dark:text-blue-200" data-id="element-759">
              Los backups se descargan automáticamente como archivos JSON
            </p>
            <p className="text-sm text-blue-600 dark:text-blue-400 mt-1" data-id="element-760">
              Guarda los archivos descargados en un lugar seguro. Para
              restaurar, usa el botón "Restaurar desde Archivo" y selecciona el
              archivo JSON del backup.
            </p>
          </div>
        </div>
      </div>

      {/* Configuración */}
      <Card data-id="element-761">
        <div className="p-6 space-y-6" data-id="element-762">
          <div data-id="element-763">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4" data-id="element-764">
              Configuración de Backups Automáticos
            </h3>

            <div className="space-y-4" data-id="element-765">
              {/* Activar/Desactivar */}
              <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-800/50 rounded-xl border border-gray-100 dark:border-gray-700/50" data-id="element-766">
                <div data-id="element-767">
                  <p className="font-semibold text-gray-900 dark:text-white" data-id="element-768">
                    Backups Automáticos
                  </p>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-1" data-id="element-769">
                    Crear backups automáticamente según la frecuencia
                    configurada
                  </p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer" data-id="element-770">
                  <input type="checkbox" checked={enabled} onChange={e => setEnabled(e.target.checked)} className="sr-only peer" data-id="element-771" />

                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-blue-600" data-id="element-772"></div>
                </label>
              </div>

              {/* Frecuencia y Hora */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-773">
                <Select label="Frecuencia de Backup" options={frequencyOptions} value={frequency} onChange={value => setFrequency(value as any)} data-id="element-774" />

                <Input label="Retención (días)" type="number" value={retentionDays} onChange={e => setRetentionDays(parseInt(e.target.value) || 30)} min={1} max={365} helperText="Backups más antiguos se eliminarán automáticamente" data-id="element-775" />
              </div>

              {/* Hora programada */}
              <div className="p-4 bg-gray-50 dark:bg-gray-800/50 rounded-xl border border-gray-100 dark:border-gray-700/50" data-id="element-776">
                <p className="text-sm font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-2" data-id="element-777">
                  <ClockIcon className="w-4 h-4 text-blue-500" data-id="element-778" />
                  Hora del backup automático
                </p>
                <div className="flex items-center gap-3" data-id="element-779">
                  <div className="flex flex-col gap-1" data-id="element-780">
                    <label className="text-xs text-gray-500 dark:text-gray-400" data-id="element-781">
                      Hora (0–23)
                    </label>
                    <input type="number" min={0} max={23} value={backupHour} onChange={e => setBackupHour(Math.min(23, Math.max(0, parseInt(e.target.value) || 0)))} className="w-20 px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm font-mono text-center focus:outline-none focus:ring-2 focus:ring-blue-500" data-id="element-782" />
                  </div>
                  <span className="text-gray-400 text-xl font-bold mt-5" data-id="element-783">
                    :
                  </span>
                  <div className="flex flex-col gap-1" data-id="element-784">
                    <label className="text-xs text-gray-500 dark:text-gray-400" data-id="element-785">
                      Minuto (0–59)
                    </label>
                    <input type="number" min={0} max={59} value={backupMinute} onChange={e => setBackupMinute(Math.min(59, Math.max(0, parseInt(e.target.value) || 0)))} className="w-20 px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm font-mono text-center focus:outline-none focus:ring-2 focus:ring-blue-500" data-id="element-786" />
                  </div>
                  <div className="mt-5 ml-2 text-sm text-gray-500 dark:text-gray-400" data-id="element-787">
                    = {String(backupHour).padStart(2, '0')}:
                    {String(backupMinute).padStart(2, '0')} hrs
                  </div>
                </div>
                <p className="text-xs text-gray-400 dark:text-gray-500 mt-2" data-id="element-788">
                  El backup se ejecutará a esta hora según la frecuencia
                  seleccionada.
                </p>
              </div>

              {/* Información del último backup y próximo backup */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-789">
                {config?.lastBackupAt && <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-xl border border-blue-100 dark:border-blue-800" data-id="element-790">
                    <div className="flex items-center gap-2 text-blue-700 dark:text-blue-300" data-id="element-791">
                      <ClockIcon className="w-4 h-4" data-id="element-792" />
                      <span className="text-sm font-medium" data-id="element-793">
                        Último backup:{' '}
                        {new Date(config.lastBackupAt).toLocaleString('es-ES')}
                      </span>
                    </div>
                  </div>}
                {config?.enabled && config?.nextBackupAt && <div className="p-4 bg-green-50 dark:bg-green-900/20 rounded-xl border border-green-100 dark:border-green-800" data-id="element-794">
                    <div className="flex items-center gap-2 text-green-700 dark:text-green-300" data-id="element-795">
                      <CalendarIcon className="w-4 h-4" data-id="element-796" />
                      <span className="text-sm font-medium" data-id="element-797">
                        Próximo backup:{' '}
                        {new Date(config.nextBackupAt).toLocaleString('es-ES')}
                      </span>
                    </div>
                  </div>}
              </div>

              {/* Auto-backup note */}
              {enabled && <div className="p-3 bg-amber-50 dark:bg-amber-900/20 rounded-lg border border-amber-200 dark:border-amber-800" data-id="element-798">
                  <p className="text-xs text-amber-700 dark:text-amber-300" data-id="element-799">
                    <strong data-id="element-800">Nota:</strong> El backup automático se ejecuta una
                    sola vez por el administrador activo, a la hora configurada.
                    Solo se descargará en el navegador del administrador que
                    esté conectado en ese momento. Si no hay sesión activa, el
                    backup se ejecutará en la próxima sesión del administrador.
                  </p>
                </div>}
            </div>
          </div>

          {/* Botones de acción */}
          <div className="flex items-center gap-3 pt-4 border-t border-gray-100 dark:border-gray-700" data-id="element-801">
            <Button leftIcon={<SaveIcon className="w-4 h-4" data-id="element-803" />} onClick={handleSaveConfig} isLoading={saving} data-id="element-802">
              Guardar Configuración
            </Button>
            <Button variant="outline" leftIcon={<TrashIcon className="w-4 h-4" data-id="element-805" />} onClick={handleCleanOldBackups} data-id="element-804">
              Limpiar Backups Antiguos
            </Button>
          </div>
        </div>
      </Card>

      {/* Historial de Backups */}
      <Card data-id="element-806">
        <div className="p-6" data-id="element-807">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2" data-id="element-808">
            <RefreshCwIcon className="w-5 h-5" data-id="element-809" />
            Historial de Backups ({history.length})
          </h3>

          {history.length === 0 ? <div className="py-12 text-center text-gray-500 dark:text-gray-400" data-id="element-810">
              <DatabaseIcon className="w-12 h-12 mx-auto mb-3 opacity-30" data-id="element-811" />
              <p data-id="element-812">No hay backups disponibles</p>
              <p className="text-sm mt-1" data-id="element-813">
                Crea tu primer backup usando el botón superior
              </p>
            </div> : <div className="overflow-x-auto" data-id="element-814">
              <table className="w-full" data-id="element-815">
                <thead className="bg-gray-50 dark:bg-gray-800/50 border-b border-gray-200 dark:border-gray-700" data-id="element-816">
                  <tr data-id="element-817">
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase" data-id="element-818">
                      Versión
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase" data-id="element-819">
                      Tipo
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase" data-id="element-820">
                      Fecha
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase" data-id="element-821">
                      Tamaño
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase" data-id="element-822">
                      Creado por
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase" data-id="element-823">
                      Estado
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase" data-id="element-824">
                      Acciones
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700" data-id="element-825">
                  {history.map(backup => <tr key={backup.id} className="hover:bg-gray-50 dark:hover:bg-gray-800/50" data-id="element-826">
                      <td className="px-4 py-3" data-id="element-827">
                        <span className="font-mono text-sm font-semibold text-blue-600 dark:text-blue-400" data-id="element-828">
                          v{backup.versionNumber}
                        </span>
                      </td>
                      <td className="px-4 py-3" data-id="element-829">
                        <Badge variant={backup.backupType === 'automatic' ? 'info' : 'default'} data-id="element-830">
                          {backup.backupType === 'automatic' ? 'Automático' : 'Manual'}
                        </Badge>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400" data-id="element-831">
                        {new Date(backup.createdAt).toLocaleString('es-ES')}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400" data-id="element-832">
                        {formatFileSize(backup.fileSize)}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400" data-id="element-833">
                        {backup.createdByUserName || 'Sistema'}
                      </td>
                      <td className="px-4 py-3" data-id="element-834">
                        {backup.restoredAt ? <Badge variant="success" data-id="element-835">
                            <CheckCircleIcon className="w-3 h-3 mr-1" data-id="element-836" />
                            Restaurado
                          </Badge> : backup.status === 'completed' ? <Badge variant="success" data-id="element-837">Completado</Badge> : backup.status === 'failed' ? <Badge variant="danger" data-id="element-838">Fallido</Badge> : <Badge variant="warning" data-id="element-839">En progreso</Badge>}
                      </td>
                      <td className="px-4 py-3" data-id="element-840">
                        <div className="flex items-center justify-end gap-2" data-id="element-841">
                          <button onClick={() => {
                      setSelectedBackup(backup);
                      setIsDeleteModalOpen(true);
                    }} className="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors" title="Eliminar" data-id="element-842">
                            <TrashIcon className="w-4 h-4" data-id="element-843" />
                          </button>
                        </div>
                      </td>
                    </tr>)}
                </tbody>
              </table>
            </div>}
        </div>
      </Card>

      {/* Modal de Restauración desde Archivo */}
      <Modal isOpen={isRestoreModalOpen} onClose={() => !restoring && setIsRestoreModalOpen(false)} title="Restaurar Backup desde Archivo" size="sm" icon={<UploadIcon className="w-5 h-5" data-id="element-845" />} accentColor="amber" data-id="element-844">
        <div className="space-y-4" data-id="element-846">
          <div className="p-4 bg-amber-50 dark:bg-amber-900/20 rounded-lg border border-amber-200 dark:border-amber-800" data-id="element-847">
            <div className="flex items-start gap-3" data-id="element-848">
              <AlertTriangleIcon className="w-5 h-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" data-id="element-849" />
              <div data-id="element-850">
                <p className="text-sm font-medium text-amber-800 dark:text-amber-200" data-id="element-851">
                  ⚠️ Advertencia: Esta acción sobrescribirá todos los datos
                  actuales
                </p>
                <p className="text-sm text-amber-600 dark:text-amber-400 mt-2" data-id="element-852">
                  Selecciona un archivo JSON de backup previamente descargado.
                  Se recomienda crear un backup manual antes de restaurar.
                </p>
              </div>
            </div>
          </div>

          {/* File upload area */}
          <div className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-xl p-6 text-center cursor-pointer hover:border-blue-400 dark:hover:border-blue-500 transition-colors" onClick={() => fileInputRef.current?.click()} data-id="element-853">
            <input ref={fileInputRef} type="file" accept=".json" onChange={handleFileChange} className="hidden" data-id="element-854" />

            <UploadIcon className="w-8 h-8 mx-auto text-gray-400 mb-2" data-id="element-855" />
            {restoreFile ? <div data-id="element-856">
                <p className="text-sm font-medium text-gray-900 dark:text-white" data-id="element-857">
                  {restoreFile.name}
                </p>
                <p className="text-xs text-gray-500 mt-1" data-id="element-858">
                  {formatFileSize(restoreFile.size)}
                </p>
              </div> : <div data-id="element-859">
                <p className="text-sm text-gray-600 dark:text-gray-400" data-id="element-860">
                  Haz clic para seleccionar un archivo JSON de backup
                </p>
                <p className="text-xs text-gray-400 mt-1" data-id="element-861">
                  savidna-backup-vX-YYYY-MM-DD.json
                </p>
              </div>}
          </div>

          {restoreFileError && <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg" data-id="element-862">
              <p className="text-sm text-red-600 dark:text-red-400" data-id="element-863">
                {restoreFileError}
              </p>
            </div>}

          <div className="flex justify-end gap-3" data-id="element-864">
            <Button variant="outline" onClick={() => setIsRestoreModalOpen(false)} disabled={restoring} data-id="element-865">
              Cancelar
            </Button>
            <Button variant="primary" onClick={handleRestoreFromFile} isLoading={restoring} disabled={!restoreFile} leftIcon={<RotateCcwIcon className="w-4 h-4" data-id="element-867" />} data-id="element-866">
              Restaurar Backup
            </Button>
          </div>
        </div>
      </Modal>

      {/* Modal de Eliminación */}
      <Modal isOpen={isDeleteModalOpen} onClose={() => setIsDeleteModalOpen(false)} title="Eliminar Backup" size="sm" icon={<TrashIcon className="w-5 h-5" data-id="element-869" />} accentColor="rose" data-id="element-868">
        <div className="space-y-4" data-id="element-870">
          <p className="text-gray-600 dark:text-gray-400" data-id="element-871">
            ¿Estás seguro de que deseas eliminar el registro del backup{' '}
            <strong data-id="element-872">v{selectedBackup?.versionNumber}</strong>?
          </p>
          <p className="text-sm text-gray-500 dark:text-gray-500" data-id="element-873">
            Esto solo elimina el registro del historial. El archivo descargado
            no se verá afectado.
          </p>

          <div className="flex justify-end gap-3 pt-2" data-id="element-874">
            <Button variant="outline" onClick={() => setIsDeleteModalOpen(false)} data-id="element-875">
              Cancelar
            </Button>
            <Button variant="danger" onClick={handleDeleteBackup} data-id="element-876">
              Eliminar
            </Button>
          </div>
        </div>
      </Modal>
    </div>;
}